/* ==========================================================================
   OB Review Overlay
   - Makes .ob-rating clickable (both DOM instances).
   - On open: borrows the two review sections into a slide-up card.
   - On close: returns them to their exact original inline position.
   Judge.me initialises normally inline on load (nodes only leave while open).
   ========================================================================== */
(function () {
  'use strict';

  if (window.__obReviewOverlayInit) return;
  window.__obReviewOverlayInit = true;

  var DURATION_FALLBACK = 550; // ms; slightly > --duration-long (500ms)

  function init() {
    var triggers = document.querySelectorAll('.ob-rating');
    if (!triggers.length) return;

    // Identify the review nodes to borrow: the "Reviews" heading/pill custom-liquid
    // section and the Judge.me apps section. Match by their stable inner markers.
    var reviewNodes = collectReviewSections();
    if (!reviewNodes.length) return; // nothing to show; leave rating non-clickable

    var overlay = buildOverlay();
    document.body.appendChild(overlay.root);

    // Wire triggers
    Array.prototype.forEach.call(triggers, function (el) {
      el.classList.add('ob-rating--clickable');
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-haspopup', 'dialog');
      el.setAttribute('aria-label', 'Open customer reviews');
      el.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          open();
        }
      });
    });

    /* ---- Delegated fallback: cloned .ob-rating (floating CTA) ----------------
       snippets/ob-floating-cta.liquid CLONES the product info block into the
       sticky side panel. cloneNode() does not copy event listeners, so the
       per-trigger handlers wired above never exist on the clone. The snippet's
       own click proxy only forwards nodes it finds in its pairList, and the
       star <svg> nodes are not reliably paired (an <svg> e.target also behaves
       differently from HTML elements for .matches()/.closest()), so star clicks
       died silently while the "5.0 / 5" and subtitle spans proxied fine.

       A single document-level listener catches BOTH the inline and any cloned
       .ob-rating, for every template that renders one. Capture phase so it runs
       before the floating CTA's own proxy. (DS 2026-08-06) */
    document.addEventListener('click', function (e) {
      var t = e.target;
      // SVG elements: .closest() exists on modern browsers, but guard anyway
      // and walk up manually via parentNode (SVG parentElement is reliable).
      var hit = null;
      var node = t;
      while (node && node !== document) {
        if (node.classList && node.classList.contains('ob-rating')) { hit = node; break; }
        node = node.parentNode;
      }
      if (!hit) return;
      // The inline instance already has its own bound handler; let it run and
      // bail here so we never double-fire (open() is idempotent, but keep clean).
      if (Array.prototype.indexOf.call(triggers, hit) !== -1) return;
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);

    var isOpen = false;
    var markers = [];

    function borrowIn() {
      markers = [];
      reviewNodes.forEach(function (node) {
        var marker = document.createElement('span');
        marker.className = 'ob-review-overlay__marker';
        marker.style.display = 'none';
        node.parentNode.insertBefore(marker, node);
        markers.push({ marker: marker, node: node });
        overlay.body.appendChild(node);
      });
    }

    function returnOut() {
      markers.forEach(function (pair) {
        if (pair.marker && pair.marker.parentNode) {
          pair.marker.parentNode.insertBefore(pair.node, pair.marker);
          pair.marker.parentNode.removeChild(pair.marker);
        }
      });
      markers = [];
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      borrowIn();
      // Force reflow so the slide transition runs from the closed state.
      overlay.root.getBoundingClientRect();
      overlay.root.classList.add('is-open');
      document.body.classList.add('ob-review-overlay-open');
      overlay.close.focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      overlay.root.classList.remove('is-open');
      document.body.classList.remove('ob-review-overlay-open');
      // Return nodes only after the slide-down finishes (no flash).
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        returnOut();
      };
      var onEnd = function (e) {
        if (e.target === overlay.card && e.propertyName === 'transform') {
          overlay.card.removeEventListener('transitionend', onEnd);
          finish();
        }
      };
      overlay.card.addEventListener('transitionend', onEnd);
      // Safety fallback if transitionend never fires.
      setTimeout(finish, DURATION_FALLBACK);
    }

    overlay.close.addEventListener('click', close);
    overlay.backdrop.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (isOpen && e.key === 'Escape') close();
    });
  }

  function collectReviewSections() {
    // IMPORTANT: borrow the outer .shopify-section WRAPPERS, never the inner
    // Judge.me nodes. Moving .jdgm-rev-widg directly makes Judge.me re-init and
    // wipe the rendered reviews. Moving the whole section wrapper is safe.
    var nodes = [];
    // 1) The "Reviews" heading + verified pill (custom-liquid section).
    var heading = document.querySelector('.custom_review_product');
    if (heading) {
      var headingSection = heading.closest('.shopify-section');
      if (headingSection && nodes.indexOf(headingSection) === -1) nodes.push(headingSection);
    }
    // 2) The Judge.me widget apps section (whole wrapper).
    var jdgm = document.querySelector('.jdgm-rev-widg') ||
               document.querySelector('.jdgm-widget') ||
               document.querySelector('[class*="jdgm-"]');
    if (jdgm) {
      var jdgmSection = jdgm.closest('.shopify-section');
      if (jdgmSection && nodes.indexOf(jdgmSection) === -1) nodes.push(jdgmSection);
    }
    return nodes;
  }

  function buildOverlay() {
    var root = document.createElement('div');
    root.className = 'ob-review-overlay';
    root.setAttribute('aria-hidden', 'true');

    var backdrop = document.createElement('div');
    backdrop.className = 'ob-review-overlay__backdrop';

    var card = document.createElement('div');
    card.className = 'ob-review-overlay__card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-label', 'Customer reviews');

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'ob-review-overlay__close';
    close.setAttribute('aria-label', 'Close reviews');
    close.innerHTML =
      '<svg class="icon icon-close" aria-hidden="true" focusable="false" ' +
      'viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.433 7.579 7.579a.5.5 0 ' +
      '00.707-.707l-7.58-7.58 7.434-7.432a.5.5 0 00-.707-.707L9.005 8.02 1.573.588a.5.5 ' +
      '0 00-.708.707l7.433 7.433-7.433 7.25z" fill="currentColor"/></svg>';

    var body = document.createElement('div');
    body.className = 'ob-review-overlay__body';

    card.appendChild(close);
    card.appendChild(body);
    root.appendChild(backdrop);
    root.appendChild(card);

    return { root: root, backdrop: backdrop, card: card, close: close, body: body };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
